// src/features/my-shift/calendar.ts
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, format,
} from "date-fns";
import type { Shift } from "@/features/shift/types";
import type { ShiftRequest } from "@/features/shift-request/types";
import { intlLocale } from "@/i18n/dateFormat";

const WEEK_OPTS = { weekStartsOn: 1 as const }; // 周一为一周起点

export interface DateRange { start: string; end: string; }

/** 月视图 range：当月首尾补齐到整周（周一~周日），返回 YYYY-MM-DD */
export function getMonthRange(anchor: Date): DateRange {
  const start = startOfWeek(startOfMonth(anchor), WEEK_OPTS);
  const end = endOfWeek(endOfMonth(anchor), WEEK_OPTS);
  return { start: format(start, "yyyy-MM-dd"), end: format(end, "yyyy-MM-dd") };
}

/** 周视图 range：anchor 所在周（周一~周日） */
export function getWeekRange(anchor: Date): DateRange {
  const start = startOfWeek(anchor, WEEK_OPTS);
  const end = endOfWeek(anchor, WEEK_OPTS);
  return { start: format(start, "yyyy-MM-dd"), end: format(end, "yyyy-MM-dd") };
}

export function scheduleDateOf(s: Shift): string {
  return s.scheduleDate || s.schedule_date || "";
}

/** scheduleDate → Shift（同一天理论上一条） */
export function groupByDate(schedules: Shift[]): Record<string, Shift> {
  const m: Record<string, Shift> = {};
  for (const s of schedules) {
    const d = scheduleDateOf(s);
    // 同一天至多一条，后发覆盖前发（后端不变式）
    if (d) m[d] = s;
  }
  return m;
}

/** 有 pending 申请的 scheduleDate 集合（用于月历 ⚡ 角标） */
export function pendingDateSet(schedules: Shift[], requests: ShiftRequest[]): Set<string> {
  const byId = new Map<string, string>(); // scheduleId → date
  for (const s of schedules) byId.set(s.id, scheduleDateOf(s));
  const set = new Set<string>();
  for (const r of requests) {
    if (r.status !== "pending") continue;
    const sid = r.scheduleId || r.schedule_id;
    const d = sid ? byId.get(sid) : undefined;
    if (d) set.add(d);
  }
  return set;
}

/**
 * ISO → "2:00 PM"（指定时区）；空值 → ""
 * 与 `formatShiftTime`（@/features/shift/types）的区别：本函数 en 下强制 12 小时制且 tz 为必填；
 * `formatShiftTime` 输出 24h 格式且 tz 可选。
 * locale 传界面语言（i18n.language）：en 维持 "2:00 PM"；zh / pt / es 按各自 locale 的时制（"14:00"），
 * 与 utils/dateFormat 的 formatTime 口径一致。不传按 en。
 */
export function formatTime12(value: string | null | undefined, tz: string, locale?: string): string {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const intl = intlLocale(locale);
  return d.toLocaleTimeString(intl, {
    hour: "numeric", minute: "2-digit", timeZone: tz, ...(intl === "en-US" ? { hour12: true } : {}),
  });
}
